import React from 'react';
import { Page as RPDFPage, View, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontFamily, pdfFontSize, pdfPageFrame, pdfSpacing } from '../theme';
import type { DocumentMeta, SchoolBranding } from '../types';
import { Masthead, RunningHeader } from './Header';
import OfficialHeader, { type OfficialDetail } from './OfficialHeader';
import Footer from './Footer';
import Watermark from './Watermark';

type Orientation = 'portrait' | 'landscape';
type Size = 'A4' | 'A5' | 'LETTER';

interface Props {
  branding: SchoolBranding;
  meta: DocumentMeta;
  orientation?: Orientation;
  size?: Size;
  /**
   * Wear the school's official letterhead (as letters and receipts do)
   * instead of the masthead. Its details line says who or what the document
   * is about; left out, it's the subtitle.
   */
  official?: { details?: OfficialDetail[]; perRow?: number };
  children: React.ReactNode;
}

const styles = StyleSheet.create({
  page: {
    paddingTop: pdfPageFrame.contentTop,
    paddingBottom: pdfPageFrame.contentBottom,
    paddingHorizontal: pdfSpacing.pageX,
    fontFamily: pdfFontFamily.sans,
    fontSize: pdfFontSize.sm,
    color: pdfColors.textPrimary,
    backgroundColor: pdfColors.surface,
  },
  // Page 1 has no running header, so the letterhead pulls up into that band — as the masthead does.
  official: { marginTop: pdfPageFrame.mastheadTop - pdfPageFrame.contentTop, marginBottom: pdfSpacing.sm },
});

const officialDetails = (meta: DocumentMeta, details?: OfficialDetail[]): OfficialDetail[] =>
  details ?? (meta.subtitle ? [{ value: meta.subtitle }] : []);

/**
 * Standard portal page. Owns the page frame (see `pdfPageFrame`): masthead
 * on page 1, running header on pages 2+, footer on every page, and padding
 * that reserves both bands — so no document has to think about them.
 *
 * Page-break rules every document body must follow (react-pdf splits any
 * wrappable View at a page edge, and a split `flexDirection: 'row'` View
 * draws its halves over the footer):
 *   - Every horizontal row (field pairs, cards side by side, table rows)
 *     gets `wrap={false}` — it moves to the next page whole.
 *   - Section headings use `minPresenceAhead` (Section does this) so a
 *     heading is never stranded at the bottom of a page.
 *   - Never set `lineHeight` on the page itself: react-pdf silently drops
 *     the footer's dynamic "Page N of M" text when it inherits one.
 */
const Page: React.FC<Props> = ({ branding, meta, orientation = 'portrait', size = 'A4', official, children }) => (
  <RPDFPage size={size} orientation={orientation} style={styles.page} wrap>
    <RunningHeader branding={branding} meta={meta} />
    {meta.watermark && <Watermark text={meta.watermark} />}
    {official ? (
      <View style={styles.official}>
        <OfficialHeader branding={branding} title={meta.title} details={officialDetails(meta, official.details)} perRow={official.perRow} />
      </View>
    ) : (
      <Masthead branding={branding} meta={meta} />
    )}
    {children}
    <Footer meta={meta} />
  </RPDFPage>
);

export default Page;
